import { ZENDESK_WIDGET_SELECTOR } from '@/components/business/SupportChat/providers/zendesk/widget'
import {
  FLOATING_BOTTOM_BAR_HEIGHT_PX,
  FLOATING_CHAT_LAUNCHER_STACK_SELECTORS,
  FLOATING_CORNER_LAUNCHER_BOTTOM_OFFSET_PX,
  FLOATING_CORNER_LAUNCHER_HORIZONTAL_OFFSET_PX,
  FLOATING_SURVEY_BAR_HEIGHT_CSS_VAR,
  FLOATING_SURVEY_ROOT_SELECTOR,
} from './floatingWidgetsLayout'

const STACK_MANAGED_ATTR = 'data-floating-stack-managed'
export const DEV_CHAT_MOCK_ACTIVE_ATTR = 'data-ze-dev-chat-mock-active'
const DEV_FLOATING_WIDGET_MOCK_ATTR = 'data-ze-dev-floating-widget-mock'
const PRIMARY_LAUNCHER_IDS = ['launcher', 'spr-live-chat-app'] as const
const ZENDESK_SHELL_IDS = new Set(['launcher', 'launcher-frame'])
const STACK_REAPPLY_MS = 1000
const ZENDESK_OFFSET_POLL_MS = 250
const ZENDESK_OFFSET_POLL_MAX = 40

let stackReapplyInterval = 0
let managedLaunchers = new Set<HTMLElement>()
let observer: MutationObserver | null = null
let scheduledFrame = 0
let activeStop: (() => void) | null = null
let zendeskOffsetPollTimeout = 0
let zendeskOffsetPollAttempts = 0
let pendingZendeskVerticalPx = FLOATING_CORNER_LAUNCHER_BOTTOM_OFFSET_PX
let pendingZendeskHorizontalPx = FLOATING_CORNER_LAUNCHER_HORIZONTAL_OFFSET_PX

const isConnected = (element: HTMLElement): boolean =>
  element.isConnected && document.body.contains(element)

const isHidden = (element: HTMLElement): boolean => {
  const style = window.getComputedStyle(element)
  return style.display === 'none' || style.visibility === 'hidden'
}

const shouldIncludeLauncher = (element: HTMLElement): boolean => {
  if (!isConnected(element)) return false
  if (ZENDESK_SHELL_IDS.has(element.id)) return true
  return !isHidden(element)
}

const isDevChatMockCoveringProdLauncher = (): boolean =>
  document.body.getAttribute(DEV_CHAT_MOCK_ACTIVE_ATTR) === 'true'

const isDevMockElement = (element: Element): boolean =>
  element.hasAttribute(DEV_FLOATING_WIDGET_MOCK_ATTR) ||
  Boolean(element.closest(`[${DEV_FLOATING_WIDGET_MOCK_ATTR}]`))

const hideProdChatInDom = (): void => {
  ZENDESK_WIDGET_SELECTOR.split(',').forEach(selector => {
    document.querySelectorAll<HTMLElement>(selector.trim()).forEach(element => {
      if (isDevMockElement(element)) return

      element.style.setProperty('display', 'none', 'important')
      element.style.setProperty('visibility', 'hidden', 'important')
      element.style.setProperty('pointer-events', 'none', 'important')
    })
  })
}

const clearProdChatDomHide = (): void => {
  ZENDESK_WIDGET_SELECTOR.split(',').forEach(selector => {
    document.querySelectorAll<HTMLElement>(selector.trim()).forEach(element => {
      if (isDevMockElement(element)) return

      element.style.removeProperty('display')
      element.style.removeProperty('visibility')
      element.style.removeProperty('pointer-events')
      element.style.removeProperty('opacity')
    })
  })
}

const showProdChatMessenger = (): void => {
  if (typeof globalThis.zE === 'function') {
    globalThis.zE('messenger', 'show')
  }
}

export const setDevChatMockCoveringLauncher = (active: boolean): void => {
  if (active) {
    document.body.setAttribute(DEV_CHAT_MOCK_ACTIVE_ATTR, 'true')
    return
  }

  document.body.removeAttribute(DEV_CHAT_MOCK_ACTIVE_ATTR)
}

export const restoreProdChatAfterDevMock = (): void => {
  setDevChatMockCoveringLauncher(false)
  resetFloatingLayoutStackState()
  clearProdChatDomHide()
  showProdChatMessenger()

  requestAnimationFrame(() => {
    syncFloatingLayout()
  })
}

export const reconcileFloatingLayoutAfterDevMock = (
  chatMockStillVisible: boolean
): void => {
  const chatMockWasActive = document.body.hasAttribute(
    DEV_CHAT_MOCK_ACTIVE_ATTR
  )

  if (chatMockWasActive && !chatMockStillVisible) {
    restoreProdChatAfterDevMock()
    return
  }

  syncFloatingLayout()
}

const clearZendeskOffsetPoll = (): void => {
  if (!zendeskOffsetPollTimeout) return
  window.clearTimeout(zendeskOffsetPollTimeout)
  zendeskOffsetPollTimeout = 0
  zendeskOffsetPollAttempts = 0
}

const applyZendeskLauncherOffset = (
  verticalPx: number,
  horizontalPx: number
): boolean => {
  if (typeof globalThis.zE !== 'function') return false

  globalThis.zE('messenger:set', 'customization', {
    position: {
      side: 'right',
      offset: {
        web: { vertical: verticalPx, horizontal: horizontalPx },
        mobile: { vertical: verticalPx, horizontal: horizontalPx },
      },
    },
  })

  return true
}

const scheduleZendeskLauncherOffset = (
  verticalPx: number,
  horizontalPx: number
): void => {
  pendingZendeskVerticalPx = verticalPx
  pendingZendeskHorizontalPx = horizontalPx

  if (applyZendeskLauncherOffset(verticalPx, horizontalPx)) {
    clearZendeskOffsetPoll()
    return
  }

  if (zendeskOffsetPollTimeout) return

  const tick = (): void => {
    zendeskOffsetPollAttempts += 1

    if (
      applyZendeskLauncherOffset(
        pendingZendeskVerticalPx,
        pendingZendeskHorizontalPx
      )
    ) {
      clearZendeskOffsetPoll()
      return
    }

    if (zendeskOffsetPollAttempts >= ZENDESK_OFFSET_POLL_MAX) {
      clearZendeskOffsetPoll()
      return
    }

    zendeskOffsetPollTimeout = window.setTimeout(tick, ZENDESK_OFFSET_POLL_MS)
  }

  tick()
}

export const getSurveyBarElement = (): HTMLElement | null => {
  const survey = document.querySelector<HTMLElement>(
    FLOATING_SURVEY_ROOT_SELECTOR
  )
  if (!survey || !isConnected(survey) || isHidden(survey)) return null
  return survey
}

export const getSurveyBarHeightPx = (): number => {
  const survey = getSurveyBarElement()
  if (!survey) return 0

  const measured = Math.ceil(survey.getBoundingClientRect().height)
  return measured > 0 ? measured : FLOATING_BOTTOM_BAR_HEIGHT_PX
}

export const applySurveyBarHeightCssVar = (heightPx: number): void => {
  const root = document.documentElement
  if (heightPx <= 0) {
    root.style.removeProperty(FLOATING_SURVEY_BAR_HEIGHT_CSS_VAR)
    return
  }

  root.style.setProperty(FLOATING_SURVEY_BAR_HEIGHT_CSS_VAR, `${heightPx}px`)
}

export const hasChatLauncherInDom = (): boolean =>
  getChatLauncherElements().length > 0

export const getChatLauncherElements = (): HTMLElement[] => {
  const launchers = new Set<HTMLElement>()

  for (const id of PRIMARY_LAUNCHER_IDS) {
    const element = document.getElementById(id)
    if (element && shouldIncludeLauncher(element)) launchers.add(element)
  }

  for (const selector of FLOATING_CHAT_LAUNCHER_STACK_SELECTORS) {
    document.querySelectorAll<HTMLElement>(selector).forEach(element => {
      if (shouldIncludeLauncher(element)) launchers.add(element)
    })
  }

  return [...launchers]
}

const applyLauncherPosition = (
  element: HTMLElement,
  bottomPx: number,
  rightPx: number
): void => {
  element.setAttribute(STACK_MANAGED_ATTR, 'true')
  element.style.setProperty('left', 'auto', 'important')
  element.style.setProperty(
    'bottom',
    `calc(${bottomPx}px + env(safe-area-inset-bottom, 0px))`,
    'important'
  )
  element.style.setProperty(
    'right',
    `calc(${rightPx}px + env(safe-area-inset-right, 0px))`,
    'important'
  )
  element.style.setProperty('transform', 'none', 'important')
}

const resetLauncherPosition = (element: HTMLElement): void => {
  if (!element.hasAttribute(STACK_MANAGED_ATTR)) return
  element.style.removeProperty('left')
  element.style.removeProperty('bottom')
  element.style.removeProperty('right')
  element.style.removeProperty('transform')
  element.removeAttribute(STACK_MANAGED_ATTR)
}

const ensureStackReapplyPoll = (shouldPoll: boolean): void => {
  if (!shouldPoll) {
    if (stackReapplyInterval) {
      window.clearInterval(stackReapplyInterval)
      stackReapplyInterval = 0
    }
    return
  }

  if (stackReapplyInterval) return

  stackReapplyInterval = window.setInterval(
    syncFloatingLayout,
    STACK_REAPPLY_MS
  )
}

export const resetFloatingLayoutStackState = (): void => {
  ensureStackReapplyPoll(false)

  for (const launcher of managedLaunchers) resetLauncherPosition(launcher)
  managedLaunchers.clear()
}

export const syncFloatingLayout = (): void => {
  const surveyBarHeightPx = getSurveyBarHeightPx()
  const hasSurvey = surveyBarHeightPx > 0
  const chatMockActive = isDevChatMockCoveringProdLauncher()

  applySurveyBarHeightCssVar(hasSurvey ? surveyBarHeightPx : 0)

  const hasChat = hasChatLauncherInDom()
  const shouldStack = hasSurvey && hasChat
  const bottomPx = shouldStack
    ? surveyBarHeightPx + FLOATING_CORNER_LAUNCHER_BOTTOM_OFFSET_PX
    : FLOATING_CORNER_LAUNCHER_BOTTOM_OFFSET_PX
  const rightPx = FLOATING_CORNER_LAUNCHER_HORIZONTAL_OFFSET_PX

  if (chatMockActive) {
    hideProdChatInDom()
    if (typeof globalThis.zE === 'function') {
      globalThis.zE('messenger', 'hide')
    }
  }

  if (hasChat) {
    scheduleZendeskLauncherOffset(bottomPx, rightPx)
  }

  if (chatMockActive) {
    ensureStackReapplyPoll(shouldStack)
    return
  }

  ensureStackReapplyPoll(shouldStack)

  const nextManaged = new Set<HTMLElement>()

  if (hasChat) {
    for (const launcher of getChatLauncherElements()) {
      applyLauncherPosition(launcher, bottomPx, rightPx)
      nextManaged.add(launcher)
    }
  }

  for (const launcher of managedLaunchers) {
    if (!nextManaged.has(launcher)) resetLauncherPosition(launcher)
  }

  managedLaunchers = nextManaged
}

const scheduleSync = (): void => {
  if (scheduledFrame) return
  scheduledFrame = window.requestAnimationFrame(() => {
    scheduledFrame = 0
    syncFloatingLayout()
  })
}

export const startFloatingLayoutSync = (): (() => void) => {
  if (typeof window === 'undefined') return () => undefined
  if (activeStop) return activeStop

  observer = new MutationObserver(scheduleSync)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
  })
  window.addEventListener('resize', scheduleSync)
  scheduleSync()

  const stop = () => {
    window.removeEventListener('resize', scheduleSync)
    observer?.disconnect()
    observer = null
    activeStop = null

    if (scheduledFrame) {
      window.cancelAnimationFrame(scheduledFrame)
      scheduledFrame = 0
    }

    resetFloatingLayoutStackState()
    clearZendeskOffsetPoll()
    applySurveyBarHeightCssVar(0)
  }

  activeStop = stop
  return stop
}

export const resetFloatingLayoutSyncForTests = (): void => {
  clearZendeskOffsetPoll()
  pendingZendeskVerticalPx = FLOATING_CORNER_LAUNCHER_BOTTOM_OFFSET_PX
  pendingZendeskHorizontalPx = FLOATING_CORNER_LAUNCHER_HORIZONTAL_OFFSET_PX
}
